import React, { useState } from "react"

// Import the shadcn components (adjust the import paths to match your setup)
import { Button } from "@comps/ui/button"
import { Input } from "@comps/ui/input"
import { toastError } from "@utils/toasts"
import { getWsButtonStyle, useHover } from "@utils/twClasses"
import { MessageQuestion } from "iconsax-react"
import Papa from "papaparse"
import { useTranslation } from "react-i18next"
import { translateHtml } from "src/i18n/i18n"
import { useModelStore } from "src/store"
import CompozzSpinner from "./CompozzSpinner"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip"

type CsvUploaderProps = {
    // Compact form of the parsed file: the column names once, then one value
    // array per record aligned on the columns.
    onImport: (columns: string[], rows: any[][]) => void
    loading: boolean
}

export default function CsvUploader({ onImport, loading }: CsvUploaderProps) {
    const [file, setFile] = useState<File | null>(null)
    const fileInputRef = React.useRef<HTMLInputElement | null>(null)
    const { t } = useTranslation();
    const wsColor = useModelStore((state) => state.wsColor);
    const wsDarkColor = useModelStore((state) => state.wsDarkColor);
    const validateHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
    const chooseHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

    function handleUploadClick() {
        fileInputRef.current?.click()
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            setFile(event.target.files[0])
        }
    }

    const handleUpload = () => {
        if (!file) return

        Papa.parse<string[]>(file, {
            // The first row holds the column names: they are sent once, with the
            // remaining rows as value arrays, to keep the request body small.
            header: false,
            skipEmptyLines: true,
            complete: (results) => {
                if (results.errors.length > 0) {
                    console.warn("CSV parse warnings:", results.errors)
                }
                const [header, ...rows] = results.data
                const columns = (header ?? []).map(column => String(column).trim())
                if (columns.every(column => column === "") || rows.length === 0) {
                    toastError(t("record.csv.import.error.empty"))
                    return
                }
                onImport(columns, rows)
            },
            error: (err) => {
                console.error("Error parsing CSV file:", err)
                toastError(t("record.csv.import.error"))
            }
        })
    }

    return (
        <div className="flex flex-col p-4 max-w-sm mx-auto w-3/4 gap-2">
            {loading && <div className="flex flex-col justify-center items-center h-full gap-4">
                <p className="text-sm">
                    {t("record.csv.import.loading")}
                </p>
                <CompozzSpinner />
            </div>}
            {!loading &&
                <>
                    <Input
                        ref={fileInputRef}
                        id="csvFile"
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        className="hidden"
                    />

                    <div className="flex flex-row relative w-full">
                        <Button onClick={handleUploadClick} {...chooseHover} className="h-8 min-h-8 w-full">
                            {(!file) ? t("record.csv.import.choose.file") : t("record.csv.import.change.file")}
                        </Button>
                        <TooltipProvider>
                            <Tooltip delayDuration={100}>
                                <TooltipTrigger>
                                    <MessageQuestion className="absolute -right-8 top-0 h-6 w-6 min-h-6 min-w-6 text-gray-600" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    {translateHtml(t, "record.csv.import.question.tooltip", [])}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>


                    <p className="text-sm">
                        {t("record.csv.import.selected.file")}: {(file) ? file.name : t("record.csv.import.no.file.selected")}
                    </p>

                    <Button onClick={handleUpload} disabled={!file} {...validateHover} className={"mt-8 h-8 min-h-8 " + (file ? "" : "opacity-50")}>
                        {t("record.csv.import.validate")}
                    </Button>
                </>
            }

        </div>
    )
}